import { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import Modal from '../../../components/atoms/modal/modal';
import Button from '../../../components/atoms/button';
import { describeVinLookupError, suggestVinRegex } from '../../../api/vehicles';
import { createVehicleVariant, validateVariantYears, validateVinRegex } from './functions';
import { VehicleVariant } from './types';

export type CreateVariantForm = Omit<VehicleVariant, 'variantId'>;

type CreateVariantProps = {
  brandId: number | '';
  modelId: number | '';
  brandName: string;
  brandPatterns: string[];
  modelName: string;
  open: boolean;
  onClose: () => void;
  onCreated: (variant: VehicleVariant) => void;
  /** Pre-filled suggestion, typically from the VIN lookup and the AI vehicle details. */
  initialValues?: Partial<CreateVariantForm>;
  /** When set, the modal can ask the backend for a VIN pattern and check the pattern against this VIN. */
  vin?: string;
};

const emptyForm = (modelId: number | '', initialValues?: Partial<CreateVariantForm>): CreateVariantForm => ({
  name: '',
  powertrain: 'ICE',
  fromYear: '',
  toYear: null,
  externalId: null,
  weight: 0,
  length: 0,
  height: 0,
  width: 0,
  regexPattern: '',
  ...initialValues,
  modelId: modelId === '' ? 0 : modelId,
});

const CreateVariant = ({
  brandId,
  modelId,
  brandName,
  brandPatterns,
  modelName,
  open,
  onClose,
  onCreated,
  initialValues,
  vin,
}: CreateVariantProps) => {
  const [formData, setFormData] = useState<CreateVariantForm>(emptyForm(modelId, initialValues));
  const [saving, setSaving] = useState(false);
  const [suggestingPattern, setSuggestingPattern] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Held in a ref so a new object identity from the parent cannot reset fields being edited.
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  const normalizedVin = (vin ?? '').trim().toUpperCase();
  const canSuggestPattern = normalizedVin.length === 17;

  useEffect(() => {
    if (open) {
      setFormData(emptyForm(modelId, initialValuesRef.current));
      setError(null);
    }
  }, [open, modelId]);

  const handleChange = (field: keyof CreateVariantForm, value: string | number | null) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSuggestPattern = async () => {
    if (!canSuggestPattern) return;

    setSuggestingPattern(true);
    setError(null);

    try {
      const suggestion = await suggestVinRegex(normalizedVin);
      setFormData((current) => ({
        ...current,
        regexPattern: suggestion?.regexPattern || current.regexPattern,
        name: current.name || suggestion?.variantName || '',
      }));
    } catch (suggestError) {
      console.error('Unable to suggest a VIN pattern', suggestError);
      setError(describeVinLookupError(suggestError, 'Unable to get a VIN pattern suggestion.'));
    } finally {
      setSuggestingPattern(false);
    }
  };

  const handleSave = async () => {
    if (brandId === '' || modelId === '') {
      setError('A brand and model must exist before adding a variant.');
      return;
    }

    const yearError = validateVariantYears(formData.fromYear, formData.toYear);
    if (yearError) {
      setError(yearError);
      return;
    }

    const regexPattern = formData.regexPattern.trim();
    const validationError = validateVinRegex(regexPattern, brandPatterns);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onCreated(await createVehicleVariant(brandId, modelId, { ...formData, regexPattern }));
      onClose();
    } catch (saveError) {
      console.error('Error creating vehicle variant', saveError);
      setError('Unable to create vehicle variant.');
    } finally {
      setSaving(false);
    }
  };

  // Only flag the year fields once a from year has been entered, so an untouched form looks clean.
  const yearError = formData.fromYear === ''
    ? null
    : validateVariantYears(formData.fromYear, formData.toYear);

  let patternMatchesVin: boolean | null = null;
  if (canSuggestPattern && formData.regexPattern.trim()) {
    try {
      patternMatchesVin = new RegExp(formData.regexPattern.trim(), 'i').test(normalizedVin);
    } catch {
      patternMatchesVin = false;
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create vehicle variant"
      footer={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || brandId === '' || modelId === ''}>Save</Button>
        </Box>
      }
      size="medium"
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField label="Brand" value={brandName} disabled size="small" />
        <TextField label="Model" value={modelName} disabled size="small" />
        <TextField label="Variant" value={formData.name} onChange={(event) => handleChange('name', event.target.value)} size="small" />
        <FormControl size="small">
          <InputLabel>Powertrain</InputLabel>
          <Select value={formData.powertrain} label="Powertrain" onChange={(event) => handleChange('powertrain', event.target.value)}>
            <MenuItem value="ICE">ICE</MenuItem>
            <MenuItem value="EV">EV</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="From year"
          type="number"
          value={formData.fromYear}
          onChange={(event) => handleChange('fromYear', event.target.value ? Number(event.target.value) : '')}
          size="small"
          error={Boolean(yearError)}
        />
        <TextField
          label="To year"
          type="number"
          value={formData.toYear ?? ''}
          onChange={(event) => handleChange('toYear', event.target.value ? Number(event.target.value) : null)}
          size="small"
          error={Boolean(yearError)}
          helperText={yearError ?? 'Leave empty for ongoing production'}
        />
        <TextField label="Weight (kg)" type="number" value={formData.weight} onChange={(event) => handleChange('weight', Number(event.target.value))} size="small" />
        <TextField label="Length (mm)" type="number" value={formData.length} onChange={(event) => handleChange('length', Number(event.target.value))} size="small" />
        <TextField label="Width (mm)" type="number" value={formData.width} onChange={(event) => handleChange('width', Number(event.target.value))} size="small" />
        <TextField label="Height (mm)" type="number" value={formData.height} onChange={(event) => handleChange('height', Number(event.target.value))} size="small" />
        <TextField label="MonitorId" value={formData.externalId ?? ''} onChange={(event) => handleChange('externalId', event.target.value || null)} size="small" />
        <TextField
          label="VIN pattern"
          value={formData.regexPattern}
          onChange={(event) => handleChange('regexPattern', event.target.value)}
          size="small"
          error={patternMatchesVin === false}
          helperText={patternMatchesVin === null
            ? undefined
            : patternMatchesVin ? `Matches ${normalizedVin}` : `Does not match ${normalizedVin}`}
        />
      </Box>
      {canSuggestPattern && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Button onClick={handleSuggestPattern} disabled={suggestingPattern}>
            {suggestingPattern ? 'Asking…' : 'Suggest VIN pattern'}
          </Button>
          {suggestingPattern && <CircularProgress size={20} />}
          <Typography variant="body2" color="text.secondary">
            Builds a pattern for {normalizedVin} from the VIN lookup service.
          </Typography>
        </Box>
      )}
    </Modal>
  );
};

export default CreateVariant;
